// FILE: threadStreamDelivery.ts
// Purpose: Detects lost thread-stream pushes so the client can re-snapshot instead of skipping events.
// Exports: `createThreadDeliveryTracker`, `classifyThreadDelivery`.

import type { ThreadId } from "@taskin-code/contracts";

/**
 * - `ok`: the item follows the previous one, nothing was lost.
 * - `duplicate`: the item is not newer than what we already saw (re-delivery or
 *   a reorder around a snapshot); applying it is harmless, but it must not move
 *   the cursor.
 * - `gap`: at least one push never arrived. The thread state is now incomplete —
 *   a missing `tool.completed` or turn closure never comes back on its own — so
 *   the caller must re-snapshot the thread.
 */
export type ThreadDeliveryVerdict = "ok" | "duplicate" | "gap";

/**
 * The domain event `sequence` cannot answer this question: it is the global
 * event-log id, so two consecutive events of one thread are almost never
 * consecutive numbers. The server stamps a per-(client, thread) counter on
 * every thread-stream item instead, and a jump in that counter is the only
 * proof a client has that a push was lost.
 *
 * A missing counter means the server does not stamp it (older build): the check
 * degrades to "never report a gap" rather than to false alarms.
 */
export function classifyThreadDelivery(
  previous: number | undefined,
  next: number | undefined,
): ThreadDeliveryVerdict {
  if (next === undefined || previous === undefined) {
    return "ok";
  }
  if (next <= previous) {
    return "duplicate";
  }
  return next === previous + 1 ? "ok" : "gap";
}

/**
 * What to do about a thread whose push stream has a proven hole.
 *
 * - `replay`: ask the server for that thread's events after the cursor. Cheap,
 *   and it keeps everything already on screen — including history pages the
 *   user scrolled back to, which a snapshot would discard.
 * - `snapshot`: re-subscribe and take the transcript again. The fallback for
 *   when there is no cursor to replay from.
 * - `park`: hold the event and do nothing else, because recovery is already
 *   under way or the thread is no longer subscribed.
 */
export type ThreadGapRecovery =
  | { readonly kind: "replay"; readonly fromSequenceExclusive: number }
  | { readonly kind: "snapshot" }
  | { readonly kind: "park" };

/**
 * Decide how to close a gap, given what the client knows about the thread.
 *
 * Split out from the event-router effect because the ordering of these
 * conditions is the whole correctness argument and it is not observable from
 * the outside: a second recovery started while the first is in flight would
 * race it, and a replay without a cursor would silently skip everything
 * between the thread's start and the hole.
 */
export function planThreadGapRecovery(input: {
  /** Newest event sequence already applied to the thread, if any. */
  readonly cursor: number | undefined;
  readonly subscribed: boolean;
  readonly replayInFlight: boolean;
}): ThreadGapRecovery {
  // Recovery already owns this thread; a second request would race it.
  if (input.replayInFlight) return { kind: "park" };
  // Nothing to recover into: the events keep piling up until a subscription
  // exists, which is what the pending buffer is for.
  if (!input.subscribed) return { kind: "park" };
  // Without a cursor the replay has no lower bound to ask from, and asking from
  // zero is strictly worse than the snapshot it would be reconstructing.
  if (input.cursor === undefined) return { kind: "snapshot" };
  return { kind: "replay", fromSequenceExclusive: input.cursor };
}

/**
 * The subset of buffered/replayed events that may still be applied, in order.
 *
 * Catch-up mixes two sources — events replayed from the server and live pushes
 * that arrived while the replay was in flight — and they overlap: an event can
 * legitimately appear in both. Sorting by the global `sequence` and keeping
 * only what is strictly newer than the cursor collapses that overlap without
 * needing to know which source anything came from, and guarantees the applied
 * run has no hole in the middle.
 */
export function selectApplicableThreadEvents<Event extends { readonly sequence: number }>(
  events: ReadonlyArray<Event>,
  fromSequenceExclusive: number,
): Event[] {
  const applicable: Event[] = [];
  let cursor = fromSequenceExclusive;
  for (const event of events.toSorted((left, right) => left.sequence - right.sequence)) {
    if (event.sequence <= cursor) continue;
    cursor = event.sequence;
    applicable.push(event);
  }
  return applicable;
}

export interface ThreadDeliveryTracker {
  /** Adopts the baseline a snapshot carries, replacing whatever we had. */
  readonly observeSnapshot: (threadId: ThreadId, deliverySequence: number | undefined) => void;
  /**
   * Advances the cursor for an incoming event and reports what it implies. A
   * gap also adopts the new value, so the following events do not each re-report
   * the same hole while the re-snapshot is in flight.
   */
  readonly observeEvent: (
    threadId: ThreadId,
    deliverySequence: number | undefined,
  ) => ThreadDeliveryVerdict;
  readonly forget: (threadId: ThreadId) => void;
  readonly clear: () => void;
}

export function createThreadDeliveryTracker(): ThreadDeliveryTracker {
  const deliveryByThreadId = new Map<ThreadId, number>();

  return {
    observeSnapshot: (threadId, deliverySequence) => {
      if (deliverySequence === undefined) {
        deliveryByThreadId.delete(threadId);
        return;
      }
      deliveryByThreadId.set(threadId, deliverySequence);
    },
    observeEvent: (threadId, deliverySequence) => {
      const verdict = classifyThreadDelivery(deliveryByThreadId.get(threadId), deliverySequence);
      if (deliverySequence !== undefined && verdict !== "duplicate") {
        deliveryByThreadId.set(threadId, deliverySequence);
      }
      return verdict;
    },
    forget: (threadId) => {
      deliveryByThreadId.delete(threadId);
    },
    clear: () => {
      deliveryByThreadId.clear();
    },
  };
}
